import { MapPin, Upload, Euro, Sun, BatteryCharging } from "lucide-react";

interface Step {
  title: string;
  description: string;
  Icon: typeof MapPin;
}

/**
 * Section "Démo brève" de la landing (S7-5, GAP-3 ; copy V1 refondue S16-3).
 *
 * Pas d'image bitmap : on illustre les **5 premières étapes du wizard** par des
 * icônes `lucide-react` + texte. Choix volontaire :
 * - Pas d'asset binaire à versionner (cf. contrainte brief < 200 ko).
 * - Pas d'iframe live (cf. S7-1 § 1.2, "img statique pas iframe").
 * - Schéma textuel = robuste au lecteur d'écran (alt = texte natif).
 *
 * Les libellés reprennent ceux du `WizardStepper`
 * (Adresse / Consommation / Tarifs / Panneaux / Batterie) ; l'écran final de
 * Confirmation n'est pas illustré ici. Toute modification du stepper doit être
 * répercutée ici.
 *
 * Si une capture statique est ajoutée plus tard, elle viendra remplacer
 * ce composant sans casser l'API publique (`<LandingDemo />`).
 */
const STEPS: Step[] = [
  {
    title: "1. Adresse",
    description:
      "Indiquez votre adresse pour localiser précisément votre logement et son ensoleillement.",
    Icon: MapPin,
  },
  {
    title: "2. Consommation",
    description:
      "Importez votre courbe de charge réelle, exportée depuis votre espace Enedis.",
    Icon: Upload,
  },
  {
    title: "3. Tarifs",
    description:
      "Renseignez votre contrat d'électricité (Base, Heures Creuses/Pleines ou Tempo) pour chiffrer vos économies en euros.",
    Icon: Euro,
  },
  {
    title: "4. Panneaux",
    description:
      "Précisez la puissance crête, l'inclinaison et l'orientation des panneaux envisagés.",
    Icon: Sun,
  },
  {
    title: "5. Batterie",
    description:
      "Ajoutez une batterie pour mesurer ce qu'elle apporte à votre autoconsommation.",
    Icon: BatteryCharging,
  },
];

export function LandingDemo(): JSX.Element {
  return (
    <section
      aria-labelledby="demo-title"
      className="bg-slate-50 py-16 sm:py-20"
    >
      <div className="mx-auto max-w-6xl px-4">
        <div className="mx-auto max-w-2xl text-center">
          <h2
            id="demo-title"
            className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl"
          >
            Comment ça marche ?
          </h2>
          <p className="mt-4 text-base text-slate-700">
            Cinq étapes guidées pour trouver votre configuration.
          </p>
        </div>

        <ol
          aria-label="Étapes du wizard SunMatch"
          className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
        >
          {STEPS.map(({ title, description, Icon }) => (
            <li
              key={title}
              className="relative rounded-xl border border-slate-200 bg-white p-6 shadow-sm"
            >
              {/* H7-2 : bg-amber-500 + text-white (~2.16:1) → bg-amber-700
                  (~5.32:1) pour passer WCAG AA même si l'icône est
                  `aria-hidden` (axe-core peut flagger le contraste graphique
                  SVG vs background). Cohérent avec le hero `text-amber-700`. */}
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-amber-700 text-white">
                <Icon className="h-5 w-5" aria-hidden />
              </div>
              <h3 className="mt-4 text-base font-semibold text-slate-900">
                {title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-700">
                {description}
              </p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
